import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Table, Input, Select, Button, Typography, Tag, Empty, Spin, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined, SearchOutlined, DollarOutlined,
  CheckCircleFilled, ClockCircleOutlined, WalletOutlined, FileDoneOutlined,
} from '@ant-design/icons';
import { CarLoader } from '../../components/loader';
import { feeActions } from '../../actions/feeActions';
import SpinnerLoader from '../../components/spinnerLoader';

const { Text } = Typography;

const MODE_CONFIG: Record<string, { color: string; label: string }> = {
  CASH: { color: 'green', label: 'Cash' },
  UPI: { color: 'blue', label: 'UPI' },
  CARD: { color: 'purple', label: 'Card' },
  NET_BANKING: { color: 'cyan', label: 'Net Banking' },
  CHEQUE: { color: 'orange', label: 'Cheque' },
  BANK_TRANSFER: { color: 'geekblue', label: 'Bank Transfer' },
};

const PaymentHistory: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const enrollmentId = location.state?.enrollmentId;
  const studentId = location.state?.studentId;
  const serviceType = location.state?.serviceType;

  const feeHistoryData = useSelector((state: any) => state.getEnrollmentFeeHistoryReducer);

  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState<string>('ALL');

  useEffect(() => {
    if (enrollmentId && studentId) {
      dispatch(feeActions.getEnrollmentFeeHistory({ enrollmentId, studentId }));
    }
  }, [enrollmentId, studentId, dispatch]);

  const isLoading = Boolean(feeHistoryData?.isLoading);
  const feeGroups: any[] = useMemo(() => feeHistoryData?.fees ?? [], [feeHistoryData]);

  // Flatten feeEntries from every parent fee doc into individual payment rows
  const fees: any[] = useMemo(() => {
    const rows: any[] = [];
    feeGroups.forEach((group: any) => {
      (group.feeEntries || []).forEach((entry: any) => {
        rows.push({
          ...entry,
          totalFee: group.totalFee,
          enrollmentId: group.enrollmentId,
          createdByName: entry?.createdBy?.name,
        });
      });
    });
    return rows;
  }, [feeGroups]);

  const summary = useMemo(() => {
    if (feeGroups.length === 0) return null;
    const parent = feeGroups[0];
    const totalFee = parent?.totalFee || 0;
    const totalPaid = parent?.paidAmount || 0;
    const pendingAmount = parent?.pendingAmount ?? Math.max(0, totalFee - totalPaid);
    return {
      totalFee,
      totalPaid,
      pendingAmount,
      count: fees.length,
    };
  }, [feeGroups, fees]);

  const filteredFees = useMemo(() => {
    let list = [...fees];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((f) =>
        (f.receiptNumber || '').toLowerCase().includes(q) ||
        (f.remarks || '').toLowerCase().includes(q) ||
        (f.paymentMode || '').toLowerCase().includes(q)
      );
    }
    if (modeFilter !== 'ALL') {
      list = list.filter((f) => f.paymentMode === modeFilter);
    }
    return list;
  }, [fees, search, modeFilter]);

  const timelineFees = useMemo(() => {
    return [...fees].sort((a, b) => new Date(b.paymentDate || 0).getTime() - new Date(a.paymentDate || 0).getTime());
  }, [fees]);

  const summaryCards = summary ? [
    { label: 'Total Fee', value: summary.totalFee, icon: <FileDoneOutlined />, color: '#2563eb' },
    { label: 'Total Paid', value: summary.totalPaid, icon: <CheckCircleFilled />, color: '#10b981' },
    { label: 'Pending', value: summary.pendingAmount, icon: <ClockCircleOutlined />, color: summary.pendingAmount > 0 ? '#ef4444' : '#10b981' },
    { label: 'Payments', value: summary.count, icon: <WalletOutlined />, color: '#7c4dff', raw: true },
  ] : [];

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: any, __: any, index: number) => <Text type="secondary" style={{ fontSize: 13 }}>{index + 1}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date: string) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>
            {date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {date ? new Date(date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Paid',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (amount: number) => <Text strong style={{ color: '#10b981', fontSize: 14 }}>₹{(amount || 0).toLocaleString()}</Text>,
    },
    {
      title: 'Pending',
      dataIndex: 'pendingAmount',
      key: 'pendingAmount',
      render: (amount: number) => (
        <Text style={{ color: amount > 0 ? '#f5576c' : '#10b981', fontWeight: 500 }}>₹{(amount || 0).toLocaleString()}</Text>
      ),
    },
    {
      title: 'Mode',
      dataIndex: 'paymentMode',
      key: 'paymentMode',
      render: (mode: string) => {
        const cfg = MODE_CONFIG[mode] || { color: 'default', label: mode };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'pendingAmount',
      key: 'status',
      render: (pending: number) => (
        <span className={`ph-status ph-status--${pending > 0 ? 'partial' : 'paid'}`}>
          <span className="ph-status-dot" />
          {pending > 0 ? 'Partial' : 'Cleared'}
        </span>
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (remarks: string) => (
        <Tooltip title={remarks}>
          <Text type="secondary" ellipsis style={{ maxWidth: 140, fontSize: 12 }}>{remarks || '—'}</Text>
        </Tooltip>
      ),
    },
  ];

  if (isLoading && fees.length === 0) {
    return <div className="view-loading"><SpinnerLoader /></div>;
  }

  return (
    <div className="ph-page">
      {/* Header */}
      <div className="batch-view-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/enrollments/view', { state: { enrollmentId, serviceType } })}
          className="batch-back-btn"
        >
          Enrollment
        </Button>
      </div>

      

      {/* Summary cards */}
      {summary && (
        <div className="ph-summary">
          {summaryCards.map((c, i) => (
            <div className="ph-sum-card" key={i}>
              <span className="ph-sum-icon" style={{ color: c.color, background: `${c.color}14` }}>{c.icon}</span>
              <div className="ph-sum-body">
                <span className="ph-sum-value" style={{ color: c.color }}>
                  {c.raw ? c.value : `₹${Number(c.value).toLocaleString()}`}
                </span>
                <span className="ph-sum-label">{c.label}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ph-split">
        {/* Payment log table */}
        <section className="sd-panel">
          <div className="ph-toolbar">
            <span className="batch-section-label" style={{ marginBottom: 0 }}>Transaction Log</span>
            <div className="ph-toolbar-controls">
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search remarks or mode"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 240 }}
              />
              <Select
                value={modeFilter}
                onChange={setModeFilter}
                style={{ width: 150 }}
                options={[
                  { label: 'All Modes', value: 'ALL' },
                  ...Object.entries(MODE_CONFIG).map(([value, cfg]) => ({ label: cfg.label, value })),
                ]}
              />
            </div>
          </div>

          <Spin spinning={isLoading}>
            {filteredFees.length > 0 ? (
              <Table
                className="sd-table"
                dataSource={filteredFees}
                rowKey={(r: any, i?: number) => r?._id ?? String(i)}
                columns={columns}
                size="middle"
                pagination={{ pageSize: 8, showSizeChanger: false, size: 'small' }}
              />
            ) : (
              <div className="ph-empty">
                <div className="ph-empty-icon"><DollarOutlined /></div>
                <div className="ph-empty-title">No Payments Yet</div>
                <Text type="secondary">Payment records will appear here once fees are added</Text>
              </div>
            )}
          </Spin>
        </section>

        {/* Activity timeline */}
        <section className="sd-panel">
          <span className="batch-section-label">Activity Log</span>
          {timelineFees.length > 0 ? (
            <ul className="ph-timeline">
              {timelineFees.map((f: any, i: number) => {
                const cfg = MODE_CONFIG[f.paymentMode] || { color: 'default', label: f.paymentMode };
                return (
                  <li className="ph-tl-item" key={f._id || i}>
                    <span className="ph-tl-marker"><CheckCircleFilled /></span>
                    <div className="ph-tl-body">
                      <div className="ph-tl-top">
                        <span className="ph-tl-amount">₹{(f.paidAmount || 0).toLocaleString()}</span>
                        <Tag color={cfg.color} style={{ margin: 0 }}>{cfg.label}</Tag>
                      </div>
                      <div className="ph-tl-date">
                        {f.paymentDate ? new Date(f.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </div>
                      {f.receiptNumber && <div className="ph-tl-meta">Receipt: <code>{f.receiptNumber}</code></div>}
                      {f.remarks && <div className="ph-tl-meta">{f.remarks}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity yet" />
          )}
        </section>
      </div>
    </div>
  );
};

export default PaymentHistory;
